import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { OrganisationTypeConstants } from "../../../../constants";
import { SelectData } from "../../../../models";
import { inviteUserToJoinOrganisation } from "../../../../service/identity";
import { createOrganisation, inviteOrganisationToProject } from "../../../../service/organisation";
import { searchDevelopers } from "../../../../service/query";
import { ServiceError, Status } from "../../../../service/Shared";
import { useAuth } from "../../../../useAuth";
import { Toast } from "../../../../widget";
import {
  DEFAULT_ORGANISATION_USER_ROLE,
  DEFAULT_PROJECT_INVITATION_ROLE,
  INVITE_NEW_DEVELOPER_OPTION_KEY,
  INVITE_NEW_DEVELOPER_OPTION_LABEL,
} from "./constants/inviteDeveloperModal";
import { ExistingDeveloperOption, InviteDeveloperSelectionMode } from "./models/inviteDeveloperModal";

interface UseInviteDeveloperModalProps {
  projectUuid: string;
  onClose: () => void;
  onInvitationSent?: (shouldPollForInvitation?: boolean) => void;
  show: boolean;
}

interface UseInviteDeveloperModalReturnData {
  developerOptions: SelectData;
  email: string;
  errors: ServiceError[] | undefined;
  firstName: string;
  isInviteDisabled: boolean;
  isSubmitting: boolean;
  lastName: string;
  onCancel: () => void;
  onDeveloperChange: (value: string) => void;
  onDeveloperTypeSearch: (value: string) => void;
  onEmailChange: (value: string) => void;
  onFirstNameChange: (value: string) => void;
  onLastNameChange: (value: string) => void;
  onOrganisationNameChange: (value: string) => void;
  onSendInvitation: (e: FormEvent<HTMLFormElement>) => Promise<void>;
  organisationName: string;
  selectedDeveloperKey?: string;
  shouldShowContactFields: boolean;
  shouldShowOrganisationNameField: boolean;
}

export const useInviteDeveloperModal = ({
  projectUuid,
  onClose,
  onInvitationSent,
  show,
}: UseInviteDeveloperModalProps): UseInviteDeveloperModalReturnData => {
  const { currentOrganisationUuid } = useAuth();
  const [selectedDeveloperKey, setSelectedDeveloperKey] = useState<string>();
  const [developerMap, setDeveloperMap] = useState<Record<string, ExistingDeveloperOption>>({});
  const [organisationName, setOrganisationName] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [errors, setErrors] = useState<ServiceError[] | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const developerSearchRequestId = useRef(0);

  const resetForm = useCallback((): void => {
    setSelectedDeveloperKey(undefined);
    setDeveloperMap({});
    setOrganisationName("");
    setEmail("");
    setFirstName("");
    setLastName("");
    setErrors(undefined);
  }, []);

  const handleClose = useCallback((): void => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const getDevelopers = useCallback(
    async (search: string): Promise<void> => {
      const requestId = developerSearchRequestId.current + 1;
      developerSearchRequestId.current = requestId;

      const response = await searchDevelopers({
        paging: {
          limit: 20,
          beforeCursor: "",
          afterCursor: "",
        },
        filter: {
          results: {
            displayName: {
              operator: "startsWith",
              value: search,
            },
          },
        },
        excludedProjectUuid: projectUuid,
      });

      if (requestId !== developerSearchRequestId.current) {
        return;
      }

      if (response.status === Status.Success && response.data?.results) {
        setDeveloperMap(
          response.data.results.reduce<Record<string, ExistingDeveloperOption>>((accumulator, developer) => {
            if (developer.parentOrganisation?.uuid && developer.displayName) {
              accumulator[developer.parentOrganisation.uuid] = {
                hasUsers: developer.parentOrganisation.hasUsers,
                organisationUuid: developer.parentOrganisation.uuid,
                value: developer.displayName,
              };
            }

            return accumulator;
          }, {})
        );
        return;
      }

      setDeveloperMap({});
      Toast.error({ message: "Unable to load developers" });
    },
    [projectUuid]
  );

  useEffect(() => {
    const loadDevelopers = async (): Promise<void> => {
      await getDevelopers("");
    };

    if (show) {
      loadDevelopers();
    } else {
      resetForm();
    }
  }, [getDevelopers, resetForm, show]);

  const selectionMode = useMemo<InviteDeveloperSelectionMode | undefined>(() => {
    if (!selectedDeveloperKey) {
      return undefined;
    }

    if (selectedDeveloperKey === INVITE_NEW_DEVELOPER_OPTION_KEY) {
      return InviteDeveloperSelectionMode.NewDeveloper;
    }

    if (developerMap[selectedDeveloperKey]?.hasUsers) {
      return InviteDeveloperSelectionMode.ExistingDeveloperWithUsers;
    }

    return InviteDeveloperSelectionMode.ExistingDeveloperWithoutUsers;
  }, [developerMap, selectedDeveloperKey]);

  const developerOptions = useMemo<SelectData>(
    () => [
      {
        key: INVITE_NEW_DEVELOPER_OPTION_KEY,
        value: INVITE_NEW_DEVELOPER_OPTION_LABEL,
      },
      ...Object.values(developerMap).map((developer) => ({
        key: developer.organisationUuid,
        value: developer.value,
      })),
    ],
    [developerMap]
  );

  const shouldShowOrganisationNameField = selectionMode === InviteDeveloperSelectionMode.NewDeveloper;
  const shouldShowContactFields =
    selectionMode === InviteDeveloperSelectionMode.NewDeveloper ||
    selectionMode === InviteDeveloperSelectionMode.ExistingDeveloperWithoutUsers;

  const isInviteDisabled = useMemo(() => {
    if (!selectionMode) {
      return true;
    }

    if (
      selectionMode === InviteDeveloperSelectionMode.ExistingDeveloperWithUsers &&
      selectedDeveloperKey !== undefined
    ) {
      return false;
    }

    if (selectionMode === InviteDeveloperSelectionMode.ExistingDeveloperWithoutUsers) {
      return !selectedDeveloperKey || !email || !firstName || !lastName;
    }

    return !organisationName || !email || !firstName || !lastName;
  }, [email, firstName, lastName, organisationName, selectedDeveloperKey, selectionMode]);

  const inviteSelectedOrganisationToProject = useCallback(
    async (organisationUuid: string) =>
      inviteOrganisationToProject({
        projectUuid,
        targetOrganisationUuid: organisationUuid,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        sourceOrganisationUuid: currentOrganisationUuid!,
        role: DEFAULT_PROJECT_INVITATION_ROLE,
        type: "Invitation",
        targetOrganisationEmailOverride: null,
        // This modal grants access only; the responsible owner is assigned, and a developer swapped,
        // from the project Access tab's invite page.
        makeResponsibleOwner: false,
        existingDeveloperAction: null,
      }),
    [projectUuid, currentOrganisationUuid]
  );

  const onSendInvitation = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    setErrors(undefined);
    setIsSubmitting(true);

    if (selectionMode === InviteDeveloperSelectionMode.NewDeveloper) {
      const response = await createOrganisation({
        name: organisationName,
        type: OrganisationTypeConstants.DEVELOPER,
        additionalDetails: {
          organisationUserInvitationRole: DEFAULT_ORGANISATION_USER_ROLE,
          projectOrganisationInvitationRole: DEFAULT_PROJECT_INVITATION_ROLE,
          projectUuid,
          userEmail: email,
          userFirstName: firstName,
          userLastName: lastName,
        },
      });

      setIsSubmitting(false);

      if (response.status === Status.Success) {
        Toast.success({ message: "Developer invited successfully" });
        onInvitationSent?.(true);
        handleClose();
        return;
      }

      Toast.error({ message: "Developer invitation failed" });
      setErrors(response.errors);
      return;
    }

    const selectedDeveloper = selectedDeveloperKey ? developerMap[selectedDeveloperKey] : undefined;
    if (!selectedDeveloper) {
      setIsSubmitting(false);
      setErrors([
        {
          code: "INVALID_DEVELOPER",
          message: "A valid developer must be selected",
        },
      ]);
      return;
    }

    if (selectionMode === InviteDeveloperSelectionMode.ExistingDeveloperWithoutUsers) {
      const userInvitationResponse = await inviteUserToJoinOrganisation({
        organisationUuid: selectedDeveloper.organisationUuid,
        projectUuid,
        role: DEFAULT_ORGANISATION_USER_ROLE,
        userEmail: email,
        userFirstName: firstName,
        userLastName: lastName,
      });

      if (userInvitationResponse.status !== Status.Success) {
        setIsSubmitting(false);
        Toast.error({ message: "Developer invitation failed" });
        setErrors(userInvitationResponse.errors);
        return;
      }
    }

    const projectInvitationResponse = await inviteSelectedOrganisationToProject(selectedDeveloper.organisationUuid);

    setIsSubmitting(false);

    if (projectInvitationResponse.status === Status.Success) {
      Toast.success({ message: "Developer invited successfully" });
      onInvitationSent?.();
      handleClose();
      return;
    }

    Toast.error({ message: "Developer invitation failed" });
    setErrors(projectInvitationResponse.errors);
  };

  const onCancel = (): void => {
    handleClose();
  };

  const onDeveloperChange = (value: string): void => {
    setSelectedDeveloperKey(value);
    setErrors(undefined);
  };

  const onDeveloperTypeSearch = (value: string): void => {
    setErrors(undefined);
    getDevelopers(value);
  };

  const onOrganisationNameChange = (value: string): void => {
    setOrganisationName(value);
    setErrors(undefined);
  };

  const onEmailChange = (value: string): void => {
    setEmail(value);
    setErrors(undefined);
  };

  const onFirstNameChange = (value: string): void => {
    setFirstName(value);
    setErrors(undefined);
  };

  const onLastNameChange = (value: string): void => {
    setLastName(value);
    setErrors(undefined);
  };

  return {
    developerOptions,
    email,
    errors,
    firstName,
    isInviteDisabled,
    isSubmitting,
    lastName,
    onCancel,
    onDeveloperChange,
    onDeveloperTypeSearch,
    onEmailChange,
    onFirstNameChange,
    onLastNameChange,
    onOrganisationNameChange,
    onSendInvitation,
    organisationName,
    selectedDeveloperKey,
    shouldShowContactFields,
    shouldShowOrganisationNameField,
  };
};
